import { COVER_DOC_LIMITS, type CoverTextBox } from 'fym-shared/src/music24/coverDoc';
import { normaliseDegrees, rotatePoint } from '../render/coverGeometry';
import { snapRotation } from './snapGuides';

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

/** A pointer position as shares of the cover side, from the top left corner of the cover. */
export interface StagePoint {
  x: number;
  y: number;
}

/** The pointer in the frame of the box: the centre of the box is the origin, with no rotation. Pure. */
export function toBoxFrame(point: StagePoint, box: Pick<CoverTextBox, 'x' | 'y' | 'rotationDeg'>): StagePoint {
  return rotatePoint(point.x - box.x, point.y - box.y, -box.rotationDeg);
}

/**
 * A corner handle: the box grows or shrinks about its centre. The font size and the wrap width
 * change by the same factor (the distance of the pointer from the centre, now against the start),
 * so the lines stay the same. Pure.
 */
export function resizeFromCorner(
  start: Pick<CoverTextBox, 'sizeShare' | 'width'>,
  startDistance: number,
  distance: number
): Pick<CoverTextBox, 'sizeShare' | 'width'> {
  const [minSize, maxSize] = COVER_DOC_LIMITS.sizeShare;
  const [minWidth, maxWidth] = COVER_DOC_LIMITS.width;
  const wanted = startDistance <= 0 ? 1 : distance / startDistance;
  // One factor for both, inside the limits of both, so the box keeps its lines.
  const factor = clamp(
    wanted,
    Math.max(minSize / start.sizeShare, minWidth / start.width),
    Math.min(maxSize / start.sizeShare, maxWidth / start.width)
  );
  return { sizeShare: start.sizeShare * factor, width: start.width * factor };
}

/** A side handle: only the wrap width changes, about the centre of the box. Pure. */
export function resizeFromSide(localX: number): Pick<CoverTextBox, 'width'> {
  const [minWidth, maxWidth] = COVER_DOC_LIMITS.width;
  return { width: clamp(Math.abs(localX) * 2, minWidth, maxWidth) };
}

/**
 * The round handle: the rotation that points the handle (above the box) at the pointer. It snaps
 * to a quarter turn, or to steps of 15 degrees with Shift. Pure.
 */
export function rotationFromPointer(point: StagePoint, box: Pick<CoverTextBox, 'x' | 'y'>, steps: boolean): number {
  const degrees = (Math.atan2(point.y - box.y, point.x - box.x) * 180) / Math.PI + 90;
  const turned = normaliseDegrees(degrees);
  return normaliseDegrees(steps ? Math.round(turned / 15) * 15 : snapRotation(turned));
}

/** The step of an arrow key: 0.2 % of the side, 2 % with Shift. Pure. */
export function nudgeStep(isLarge: boolean): number {
  return isLarge ? 0.02 : 0.002;
}
